import React, { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosConfig';
import '../styles/nassar-theme.css';

const Messages = () => {
    const [waService, setWaService] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    // Form state
    const [formData, setFormData] = useState({
        api_key: '',
        session: '',
        start_date: '',
        end_date: ''
    });

    // Date inputs state
    const [startDate, setStartDate] = useState({ day: '', month: '', year: '' });
    const [endDate, setEndDate] = useState({ day: '', month: '', year: '' });
    
    // Delete confirmation modal state
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    useEffect(() => {
        fetchWaService();
    }, []);

    const fetchWaService = async () => {
        try {
            setIsLoading(true);
            const response = await axiosInstance.get('/wa-service/');
            
            if (response.data && response.data.exists !== false) {
                setWaService(response.data);
                setFormData({
                    api_key: response.data.api_key || '',
                    session: response.data.session || '',
                    start_date: response.data.start_date || '',
                    end_date: response.data.end_date || ''
                });
                
                // Parse dates
                if (response.data.start_date) {
                    const [year, month, day] = response.data.start_date.split('-');
                    setStartDate({ day, month, year });
                }
                if (response.data.end_date) {
                    const [year, month, day] = response.data.end_date.split('-');
                    setEndDate({ day, month, year });
                }
            }
            setError('');
        } catch (err) {
            if (err.response?.status === 403) {
                setError('غير مصرح لك بالوصول لهذه الصفحة');
            } else {
                setError('فشل في تحميل إعدادات الواتساب');
            }
            console.error('Error fetching WaService:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (error) setError('');
        if (success) setSuccess('');
    };

    const handleDateChange = (dateType, field, value) => {
        const numericValue = value.replace(/\D/g, '');
        
        let maxLength = field === 'year' ? 4 : 2;
        let maxValue = field === 'day' ? 31 : field === 'month' ? 12 : 9999;
        let truncatedValue = numericValue.slice(0, maxLength);
        
        if (truncatedValue && parseInt(truncatedValue) > maxValue) {
            truncatedValue = String(maxValue);
        }
        
        if (dateType === 'start') {
            const newDate = { ...startDate, [field]: truncatedValue };
            setStartDate(newDate);
            updateFormDataDate('start_date', newDate);
        } else {
            const newDate = { ...endDate, [field]: truncatedValue };
            setEndDate(newDate);
            updateFormDataDate('end_date', newDate);
        }
        if (error) setError('');
        if (success) setSuccess('');
    };

    const updateFormDataDate = (fieldName, dateObj) => {
        if (dateObj.day && dateObj.month && dateObj.year && dateObj.year.length === 4) {
            const formattedDate = `${dateObj.year}-${dateObj.month.padStart(2, '0')}-${dateObj.day.padStart(2, '0')}`;
            setFormData(prev => ({ ...prev, [fieldName]: formattedDate }));
        } else {
            setFormData(prev => ({ ...prev, [fieldName]: '' }));
        }
    };

    const handleDatePickerChange = (dateType, e) => {
        const value = e.target.value;
        if (value) {
            const [year, month, day] = value.split('-');
            if (dateType === 'start') {
                setStartDate({ day, month, year });
                setFormData(prev => ({ ...prev, start_date: value }));
            } else {
                setEndDate({ day, month, year });
                setFormData(prev => ({ ...prev, end_date: value }));
            }
        }
        if (error) setError('');
        if (success) setSuccess('');
    };

    const validateForm = () => {
        if (!formData.api_key.trim()) {
            setError('الرجاء إدخال API Key');
            return false;
        }
        if (!formData.session.trim()) {
            setError('الرجاء إدخال Session');
            return false;
        }
        if (!formData.start_date) {
            setError('الرجاء إدخال تاريخ بداية الاشتراك');
            return false;
        }
        if (!formData.end_date) {
            setError('الرجاء إدخال تاريخ نهاية الاشتراك');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;

        setIsSaving(true);
        setError('');
        setSuccess('');

        try {
            const response = await axiosInstance.post('/wa-service/', formData);
            setWaService(response.data);
            setSuccess('تم حفظ إعدادات الواتساب بنجاح');
        } catch (err) {
            console.error('Error saving WaService:', err);
            setError(err.response?.data?.error || 'حدث خطأ أثناء حفظ الإعدادات');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = () => {
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        setShowDeleteModal(false);
        setIsSaving(true);
        setError('');
        setSuccess('');

        try {
            await axiosInstance.delete('/wa-service/');
            setWaService(null);
            setFormData({
                api_key: '',
                session: '',
                start_date: '',
                end_date: ''
            });
            setStartDate({ day: '', month: '', year: '' });
            setEndDate({ day: '', month: '', year: '' });
            setSuccess('تم حذف إعدادات الواتساب بنجاح');
        } catch (err) {
            console.error('Error deleting WaService:', err);
            setError(err.response?.data?.error || 'حدث خطأ أثناء حذف الإعدادات');
        } finally {
            setIsSaving(false);
        }
    };

    const cancelDelete = () => {
        setShowDeleteModal(false);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('ar-EG', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const isSubscriptionActive = () => {
        if (!waService) return false;
        return waService.is_active === true;
    };

    if (isLoading) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <p>جاري تحميل الإعدادات...</p>
            </div>
        );
    }

    return (
        <div>
            {error && (
                <div className="card" style={{ backgroundColor: '#fee', borderColor: '#fcc', marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <svg style={{ width: '24px', height: '24px', color: '#c33' }} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <p style={{ color: '#c33', margin: 0 }}>{error}</p>
                    </div>
                </div>
            )}

            {success && (
                <div className="card" style={{ backgroundColor: '#d4edda', borderColor: '#c3e6cb', marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <svg style={{ width: '24px', height: '24px', color: '#155724' }} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <p style={{ color: '#155724', margin: 0 }}>{success}</p>
                    </div>
                </div>
            )}

            {/* Status Card */}
            <div className="card" style={{ marginBottom: '1.5rem' }}>
                <div className="card-header">
                    <h3 className="card-title">
                        <svg style={{ width: '24px', height: '24px', marginLeft: '0.5rem', display: 'inline-block', color: '#25D366' }} fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                        </svg>
                        حالة اشتراك خدمة الواتساب
                    </h3>
                </div>
                
                <div style={{ padding: '1.5rem' }}>
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        gap: '1rem',
                        padding: '2rem',
                        borderRadius: '12px',
                        background: isSubscriptionActive() 
                            ? 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)' 
                            : 'linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%)',
                        border: isSubscriptionActive() 
                            ? '2px solid #28a745' 
                            : '2px solid #dc3545'
                    }}>
                        <div style={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '50%',
                            backgroundColor: isSubscriptionActive() ? '#28a745' : '#dc3545',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
                        }}>
                            {isSubscriptionActive() ? (
                                <svg style={{ width: '48px', height: '48px', color: '#fff' }} fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                            ) : (
                                <svg style={{ width: '48px', height: '48px', color: '#fff' }} fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            )}
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <h2 style={{ 
                                margin: '0 0 0.5rem 0', 
                                fontSize: '1.75rem',
                                color: isSubscriptionActive() ? '#155724' : '#721c24'
                            }}>
                                {isSubscriptionActive() ? 'الاشتراك نشط' : waService ? 'الاشتراك منتهي' : 'لا يوجد اشتراك'}
                            </h2>
                            {waService && (
                                <p style={{ 
                                    margin: 0, 
                                    color: isSubscriptionActive() ? '#155724' : '#721c24',
                                    fontSize: '1rem'
                                }}>
                                    {isSubscriptionActive() 
                                        ? `ينتهي في: ${formatDate(waService.end_date)}`
                                        : `انتهى في: ${formatDate(waService.end_date)}`
                                    }
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Settings Form Card */}
            <div className="card">
                <div className="card-header">
                    <h3 className="card-title">
                        <svg style={{ width: '24px', height: '24px', marginLeft: '0.5rem', display: 'inline-block' }} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                        </svg>
                        إعدادات خدمة الواتساب
                    </h3>
                </div>

                <form onSubmit={handleSubmit}>
                    <div style={{ padding: '1.5rem' }}>
                        {/* Row 1: API Key & Session */}
                        <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(2, 1fr)', 
                            gap: '1.5rem',
                            marginBottom: '1.5rem'
                        }}>
                            {/* API Key */}
                            <div className="form-group" style={{ marginBottom: 0 }}>
                                <label className="form-label">
                                    API Key
                                    <span style={{ color: '#c33', marginRight: '4px' }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    name="api_key"
                                    className="form-input"
                                    value={formData.api_key}
                                    onChange={handleInputChange}
                                    placeholder="أدخل API Key"
                                    dir="ltr"
                                    style={{ textAlign: 'left' }}
                                />
                            </div>

                            {/* Session */}
                            <div className="form-group" style={{ marginBottom: 0 }}>
                                <label className="form-label">
                                    Session
                                    <span style={{ color: '#c33', marginRight: '4px' }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    name="session"
                                    className="form-input"
                                    value={formData.session}
                                    onChange={handleInputChange}
                                    placeholder="أدخل Session"
                                    dir="ltr"
                                    style={{ textAlign: 'left' }}
                                />
                            </div>
                        </div>

                        {/* Row 2: Dates */}
                        <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(2, 1fr)', 
                            gap: '1.5rem'
                        }}>
                            {/* Start Date */}
                            <div className="form-group" style={{ marginBottom: 0 }}>
                                <label className="form-label">
                                    تاريخ بداية الاشتراك
                                    <span style={{ color: '#c33', marginRight: '4px' }}>*</span>
                                </label>
                                <div className="custom-date-input">
                                    <div className="date-display">
                                        <div className="date-field day-field">
                                            <span className="date-field-label">اليوم</span>
                                            <input
                                                type="text"
                                                className="date-field-input"
                                                placeholder="DD"
                                                value={startDate.day}
                                                onChange={(e) => handleDateChange('start', 'day', e.target.value)}
                                                maxLength={2}
                                            />
                                        </div>
                                        <span className="date-separator">-</span>
                                        <div className="date-field month-field">
                                            <span className="date-field-label">الشهر</span>
                                            <input
                                                type="text"
                                                className="date-field-input"
                                                placeholder="MM"
                                                value={startDate.month}
                                                onChange={(e) => handleDateChange('start', 'month', e.target.value)}
                                                maxLength={2}
                                            />
                                        </div>
                                        <span className="date-separator">-</span>
                                        <div className="date-field year-field">
                                            <span className="date-field-label">السنة</span>
                                            <input
                                                type="text"
                                                className="date-field-input"
                                                placeholder="YYYY"
                                                value={startDate.year}
                                                onChange={(e) => handleDateChange('start', 'year', e.target.value)}
                                                maxLength={4}
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            className="calendar-trigger"
                                            onClick={() => document.getElementById('start-date-picker').showPicker()}
                                        >
                                            <svg fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                                            </svg>
                                        </button>
                                        <input
                                            type="date"
                                            id="start-date-picker"
                                            className="hidden-date-input"
                                            value={formData.start_date}
                                            onChange={(e) => handleDatePickerChange('start', e)}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* End Date */}
                            <div className="form-group" style={{ marginBottom: 0 }}>
                                <label className="form-label">
                                    تاريخ نهاية الاشتراك
                                    <span style={{ color: '#c33', marginRight: '4px' }}>*</span>
                                </label>
                                <div className="custom-date-input">
                                    <div className="date-display">
                                        <div className="date-field day-field">
                                            <span className="date-field-label">اليوم</span>
                                            <input
                                                type="text"
                                                className="date-field-input"
                                                placeholder="DD"
                                                value={endDate.day}
                                                onChange={(e) => handleDateChange('end', 'day', e.target.value)}
                                                maxLength={2}
                                            />
                                        </div>
                                        <span className="date-separator">-</span>
                                        <div className="date-field month-field">
                                            <span className="date-field-label">الشهر</span>
                                            <input
                                                type="text"
                                                className="date-field-input"
                                                placeholder="MM"
                                                value={endDate.month}
                                                onChange={(e) => handleDateChange('end', 'month', e.target.value)}
                                                maxLength={2}
                                            />
                                        </div>
                                        <span className="date-separator">-</span>
                                        <div className="date-field year-field">
                                            <span className="date-field-label">السنة</span>
                                            <input
                                                type="text"
                                                className="date-field-input"
                                                placeholder="YYYY"
                                                value={endDate.year}
                                                onChange={(e) => handleDateChange('end', 'year', e.target.value)}
                                                maxLength={4}
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            className="calendar-trigger"
                                            onClick={() => document.getElementById('end-date-picker').showPicker()}
                                        >
                                            <svg fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                                            </svg>
                                        </button>
                                        <input
                                            type="date"
                                            id="end-date-picker"
                                            className="hidden-date-input"
                                            value={formData.end_date}
                                            onChange={(e) => handleDatePickerChange('end', e)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div style={{ 
                            display: 'flex', 
                            gap: '1rem', 
                            marginTop: '2rem',
                            justifyContent: 'flex-start'
                        }}>
                            <button 
                                type="submit" 
                                className="btn btn-accent btn-large"
                                disabled={isSaving}
                            >
                                {isSaving ? (
                                    <>
                                        <div className="spinner" style={{ width: '20px', height: '20px' }}></div>
                                        <span>جاري الحفظ...</span>
                                    </>
                                ) : (
                                    <>
                                        <svg style={{ width: '24px', height: '24px' }} fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        <span>حفظ الإعدادات</span>
                                    </>
                                )}
                            </button>
                            
                            {waService && (
                                <button 
                                    type="button" 
                                    className="btn btn-large"
                                    onClick={handleDelete}
                                    disabled={isSaving}
                                    style={{
                                        background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                                        color: 'white',
                                        border: 'none'
                                    }}
                                >
                                    <svg style={{ width: '24px', height: '24px' }} fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    <span>حذف الإعدادات</span>
                                </button>
                            )}
                        </div>
                    </div>
                </form>
            </div>

            {/* Info Card */}
            <div className="card" style={{ marginTop: '1.5rem', backgroundColor: '#e8f4fd', borderColor: '#bee5eb' }}>
                <div style={{ padding: '1.5rem' }}>
                    <h4 style={{ color: '#03428d', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <svg style={{ width: '24px', height: '24px' }} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        معلومات هامة
                    </h4>
                    <ul style={{ margin: 0, paddingRight: '1.5rem', color: '#0c5460', lineHeight: '1.8' }}>
                        <li>يتم استخدام خدمة WaSender API لإرسال رسائل الواتساب</li>
                        <li>يجب الحصول على API Key و Session من موقع wasenderapi.com</li>
                        <li>سيتم إرسال إشعارات تلقائية للزبائن عند إنشاء ملف تخليص جديد</li>
                        <li>سيتم إرسال إشعارات عند تحديد موعد الفحص</li>
                        <li>تأكد من صلاحية الاشتراك لضمان إرسال الرسائل</li>
                    </ul>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal-overlay" onClick={cancelDelete}>
                    <div className="modal modal-small" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '450px' }}>
                        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)' }}>
                            <h3 className="modal-title" style={{ color: 'white' }}>تأكيد الحذف</h3>
                            <button type="button" className="modal-close" onClick={cancelDelete} style={{ color: 'white' }}>
                                <svg fill="currentColor" viewBox="0 0 20 20" style={{ width: '24px', height: '24px' }}>
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div style={{ textAlign: 'center', padding: '1.5rem 1rem' }}>
                                <div style={{
                                    width: '80px',
                                    height: '80px',
                                    borderRadius: '50%',
                                    backgroundColor: '#fee',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 1.5rem',
                                    border: '3px solid #dc3545'
                                }}>
                                    <svg style={{ width: '40px', height: '40px', color: '#dc3545' }} fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <h3 style={{ 
                                    fontSize: '1.4rem', 
                                    fontWeight: '700', 
                                    color: '#2c3e50', 
                                    marginBottom: '1rem' 
                                }}>
                                    هل أنت متأكد؟
                                </h3>
                                <p style={{ 
                                    fontSize: '1rem', 
                                    color: '#6c757d', 
                                    marginBottom: '0.5rem',
                                    lineHeight: '1.6'
                                }}>
                                    سيتم حذف إعدادات خدمة الواتساب بالكامل
                                </p>
                                <p style={{ 
                                    fontSize: '0.9rem', 
                                    color: '#dc3545',
                                    fontWeight: '600'
                                }}>
                                    هذا الإجراء لا يمكن التراجع عنه!
                                </p>
                            </div>
                        </div>
                        <div className="modal-footer" style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                            <button 
                                type="button" 
                                className="btn"
                                onClick={confirmDelete}
                                style={{ 
                                    flex: 1,
                                    maxWidth: '150px',
                                    background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                                    color: 'white',
                                    border: 'none',
                                    padding: '0.75rem 1.5rem',
                                    fontSize: '1rem',
                                    fontWeight: '600'
                                }}
                            >
                                <svg style={{ width: '20px', height: '20px', marginLeft: '0.5rem' }} fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                نعم، احذف
                            </button>
                            <button 
                                type="button" 
                                className="btn btn-outline"
                                onClick={cancelDelete}
                                style={{ 
                                    flex: 1,
                                    maxWidth: '150px',
                                    background: 'white',
                                    color: '#6c757d',
                                    border: '2px solid #e1e8ed',
                                    padding: '0.75rem 1.5rem',
                                    fontSize: '1rem',
                                    fontWeight: '600'
                                }}
                            >
                                إلغاء
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Messages;
